import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, WINDOW_HEIGHT, vs} from '@/Utils/Dimensions';
import {StyleSheet} from 'react-native';

// ─── Design tokens (cream / gold dashboard) ──────────────────────────────────
const BG = CCColors.GameReview.BG;
const CARD = CCColors.GameReview.Card;
const BORDER = CCColors.GameReview.Border;
const TEXT = CCColors.GameReview.Text;
const MUTED = CCColors.GameReview.Muted;
const GOLD = CCColors.GameReview.Gold;
const GOLD_DARK = CCColors.GameReview.GoldDark;
const GREEN = CCColors.GameReview.Green;
const GREEN_BG = CCColors.GameReview.GreenBg;
const RED = CCColors.GameReview.Red;
const RED_BG = CCColors.GameReview.RedBg;
const PURPLE_BG = CCColors.GameReview.PurpleBg;
const PURPLE = CCColors.GameReview.Purple;
const ORANGE_BG = CCColors.GameReview.OrangeBg;
const RAPID_BG = CCColors.GameReview.RapidBg;
const RAPID = CCColors.GameReview.Rapid;
const BLITZ_BG = CCColors.GameReview.BlitzBg;
const BLITZ = CCColors.GameReview.Blitz;
const BLUE_TAG_BG = CCColors.GameReview.BlueTagBg;
const BLUE_TAG = CCColors.GameReview.BlueTag;

const progressBarHeight = 32;

const customStyles = StyleSheet.create({
  // ════════════════════════════════════════════════════════════════════════════
  // GAMES LIST PAGE — cream/gold dashboard
  // ════════════════════════════════════════════════════════════════════════════

  pageRoot: {
    flex: 1,
    backgroundColor: BG,
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 16,
  },

  /* ─────────────────────────────────────────────────────────────────────────
   * PREMIUM HERO BANNER — dark gradient with embedded live stats
   * ─────────────────────────────────────────────────────────────────────── */

  heroBannerContainer: {
    marginHorizontal: 24,
    marginTop: 24,
    marginBottom: 20,
  },

  // Outer shell: rich dark-brown gradient card
  heroBannerCard: {
    backgroundColor: CCColors.GameReview.HeroBannerBg,
    backgroundImage: `linear-gradient(135deg, ${CCColors.GameReview.HeroBannerGradientStart} 0%, ${CCColors.GameReview.HeroBannerGradientMid} 45%, ${CCColors.GameReview.HeroBannerGradientEnd} 100%)`,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: CCColors.Black,
    shadowOffset: {width: 0, height: 12},
    shadowOpacity: 0.28,
    shadowRadius: 24,
    elevation: 12,
  },

  // Decorative chess-board pattern overlay
  heroBannerOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 220,
    opacity: 0.06,
  },

  // Top teal accent line
  heroBannerAccentLine: {
    height: 3,
    backgroundImage: `linear-gradient(90deg, ${CCColors.Primary.Aqua} 0%, ${CCColors.Primary.Default} 60%, transparent 100%)`,
    backgroundColor: CCColors.Primary.Aqua,
  },

  heroBannerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 24,
    paddingHorizontal: 32,
    paddingTop: 28,
    paddingBottom: 24,
  },

  heroBannerLeft: {
    flex: 1,
    minWidth: 280,
  },

  // "Analysis Dashboard" eyebrow label
  heroBannerLabel: {
    fontSize: FontSizes[10],
    fontWeight: '700',
    color: CCColors.Primary.Aqua,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 8,
  },

  heroBannerTitle: {
    fontSize: FontSizes[28],
    fontWeight: '800',
    color: CCColors.GameReview.BG,
    lineHeight: 36,
    letterSpacing: -0.8,
  },

  // Golden accent on part of the title
  heroBannerTitleAccent: {
    color: CCColors.Primary.Default,
  },

  heroBannerSubtitle: {
    fontSize: FontSizes[12],
    color: updateColorWithOpacity(CCColors.GameReview.BG, 0.55),
    marginTop: 8,
    lineHeight: 19,
    maxWidth: 560,
  },

  // Horizontal divider inside banner
  heroBannerDivider: {
    height: 0.5,
    backgroundColor: updateColorWithOpacity(CCColors.GameReview.BG, 0.1),
    marginHorizontal: 32,
    marginBottom: 20,
  },

  // Embedded stats row at the bottom of the hero
  heroBannerStatsRow: {
    flexDirection: 'row',
    paddingHorizontal: 32,
    paddingBottom: 24,
    gap: 0,
  },
  heroBannerStatCell: {
    flex: 1,
    alignItems: 'flex-start',
  },
  heroBannerStatDivider: {
    width: 0.5,
    backgroundColor: updateColorWithOpacity(CCColors.GameReview.BG, 0.12),
    marginHorizontal: 16,
    alignSelf: 'stretch',
  },
  heroBannerStatNum: {
    fontSize: FontSizes[22],
    fontWeight: '700',
    color: CCColors.GameReview.BG,
    letterSpacing: -0.5,
  },
  heroBannerStatNumAccent: {
    color: CCColors.Primary.Default,
  },
  heroBannerStatLabel: {
    fontSize: FontSizes[10],
    color: updateColorWithOpacity(CCColors.GameReview.BG, 0.45),
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginTop: 2,
  },

  heroBannerRight: {
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },

  // Import button — on the dark hero it gets a gold-fill style
  btnImportPremium: {
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 28,
    borderWidth: 0,
    shadowColor: CCColors.Primary.Default,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 6,
  },

  btnImportPremiumText: {
    fontSize: FontSizes[13],
    fontWeight: '800',
    color: CCColors.GameReview.DarkGoldText,
    letterSpacing: 0.3,
  },

  controlsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginHorizontal: 24,
    marginBottom: 20,
  },
  bannerTabBar: {
    flexDirection: 'row',
    backgroundColor: CCColors.GameReview.GreyButtonBg,
    borderRadius: 10,
    padding: 4,
    gap: 2,
  },
  bannerTabBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
  },
  bannerTabBtnActive: {
    backgroundColor: CARD,
    shadowColor: CCColors.Black,
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  bannerTabBtnText: {
    fontSize: FontSizes[12],
    fontWeight: '600',
    color: MUTED,
  },
  bannerTabBtnTextActive: {
    color: TEXT,
  },
  bannerFilterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  bannerSearchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.GameReview.GreyButtonBg,
    borderRadius: 10,
    paddingHorizontal: 14,
    height: 38,
    width: 230,
  },
  searchInput: {
    flex: 1,
    fontSize: FontSizes[12],
    color: TEXT,
    outlineStyle: 'none',
  },
  btnFilters: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CCColors.GameReview.GreyButtonBg,
    borderRadius: 10,
    height: 38,
    paddingHorizontal: 14,
    justifyContent: 'center',
  },
  btnReset: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    height: 38,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  btnResetText: {
    fontSize: FontSizes[12],
    fontWeight: '600',
    color: MUTED,
  },

  // ── Section label ──────────────────────────────────────────────────────────
  sectionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginHorizontal: 24,
    marginBottom: 14,
  },
  sectionLabel: {
    fontSize: FontSizes[10],
    fontWeight: '700',
    color: MUTED,
    letterSpacing: 1,
  },
  sectionLabelLine: {
    flex: 1,
    height: 1,
    backgroundColor: BORDER,
  },

  // ── Games list container ───────────────────────────────────────────────────
  gamesList: {
    paddingHorizontal: 24,
    gap: 14,
  },

  // ── Game card ──────────────────────────────────────────────────────────────
  gameCard: {
    backgroundColor: CARD,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 18,
    paddingRight: 22,
    paddingLeft: 42,
    gap: 18,
  },
  gameCardHighlight: {
    borderColor: CCColors.GameReview.MutedGoldBorder,
  },
  gameCardPressed: {
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.22),
  },

  // ── Board accent (mini checkerboard) ───────────────────────────────────────
  boardWrap: {
    width: 66,
    height: 66,
    flexShrink: 0,
    position: 'relative',
  },
  board: {
    width: 66,
    height: 66,
    borderRadius: 10,
    overflow: 'hidden',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  boardCell: {
    width: '25%',
    height: '25%',
  },
  resultTag: {
    position: 'absolute',
    bottom: -8,
    left: '50%',
    marginLeft: -11,
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resultTagText: {
    fontSize: FontSizes[11],
    fontWeight: '800',
    color: CCColors.White,
  },

  // ── Matchup section ────────────────────────────────────────────────────────
  matchup: {
    flex: 1,
    minWidth: 0,
  },
  playersLine: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 9,
  },
  colorDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
    borderWidth: 1.5,
    borderColor: TEXT,
    backgroundColor: 'transparent',
  },
  colorDotFilled: {
    backgroundColor: TEXT,
  },
  playerName: {
    fontSize: FontSizes[13],
    fontWeight: '700',
    color: TEXT,
  },
  playerNameLink: {
    color: '#2F8E83',
    textDecorationLine: 'underline',
  },
  playerRating: {
    fontSize: FontSizes[12],
    color: MUTED,
  },
  vsSep: {
    fontSize: FontSizes[12],
    color: MUTED,
    marginHorizontal: 2,
  },
  tagsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  tag: {
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  tagText: {
    fontSize: FontSizes[11],
    fontWeight: '600',
  },
  openingTagBg: {
    backgroundColor: BLUE_TAG_BG,
    maxWidth: 280,
  },
  openingTagText: {
    color: BLUE_TAG,
  },
  plainTag: {
    fontSize: FontSizes[11],
    fontWeight: '600',
    color: MUTED,
  },

  // ── Accuracy ring ──────────────────────────────────────────────────────────
  accuracyWrapper: {
    width: 60,
    alignItems: 'center',
    flexShrink: 0,
  },
  ringWrapper: {
    width: 54,
    height: 54,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  ringValue: {
    position: 'absolute',
    fontSize: FontSizes[15],
    fontWeight: '800',
  },
  ringLabel: {
    fontSize: FontSizes[10],
    color: MUTED,
  },
  notAnalyzedText: {
    fontSize: FontSizes[10],
    color: MUTED,
    textAlign: 'center',
    lineHeight: 14,
  },

  accuracyCol: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 165,
    flexShrink: 0,
  },
  accuracyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 18,
  },
  accuracyHeaderText: {
    fontSize: FontSizes[9],
    color: MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  accuracyVsText: {
    fontSize: FontSizes[10],
    color: MUTED,
    fontWeight: '600',
    marginTop: 20,
  },

  verticalDivider: {
    width: 1,
    height: 40,
    backgroundColor: BORDER,
    marginHorizontal: 8,
  },

  timeCol: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 90,
    flexShrink: 0,
    gap: 4,
  },
  timeHeaderText: {
    fontSize: FontSizes[9],
    color: MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  timeControlVal: {
    fontSize: FontSizes[10],
    color: MUTED,
    fontWeight: '600',
  },

  // ── Date column ────────────────────────────────────────────────────────────
  dateCol: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 135,
    flexShrink: 0,
  },
  dateHeaderText: {
    fontSize: FontSizes[9],
    color: MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  dateMain: {
    fontSize: FontSizes[12],
    fontWeight: '700',
    color: TEXT,
    textAlign: 'center',
    marginBottom: 3,
  },
  dateSub: {
    fontSize: FontSizes[11],
    color: MUTED,
    textAlign: 'center',
  },

  // ── Puzzle progress column ─────────────────────────────────────────────────
  puzzleCol: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 130,
    flexShrink: 0,
  },
  puzzleHeaderText: {
    fontSize: FontSizes[9],
    color: MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  actionCol: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: 115,
    flexShrink: 0,
  },
  puzzleLabel: {
    fontSize: FontSizes[11],
    color: TEXT,
    textAlign: 'center',
    marginBottom: 6,
  },
  puzzleLabelMuted: {
    fontSize: FontSizes[11],
    color: MUTED,
    textAlign: 'center',
  },
  progressTrack: {
    height: 5,
    width: 110,
    backgroundColor: CCColors.GameReview.GreyButtonBg,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 5,
    alignSelf: 'center',
  },
  progressFill: {
    height: '100%',
    backgroundColor: GOLD,
    borderRadius: 4,
  },
  puzzlePct: {
    fontSize: FontSizes[10],
    color: MUTED,
    textAlign: 'center',
  },

  // ── Favourite star button ──────────────────────────────────────────────────
  starBtn: {
    width: 38,
    height: 38,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CARD,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  starBtnActive: {
    borderColor: CCColors.GameReview.LightMutedGoldBorder,
  },

  // ── Action buttons ─────────────────────────────────────────────────────────
  actionBtn: {
    borderRadius: 9,
    paddingVertical: 8,
    paddingHorizontal: 14,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  actionBtnBase: {
    fontSize: FontSizes[12],
    fontWeight: '700',
  },
  actionBtnReview: {
    backgroundColor: GOLD,
  },
  actionBtnReviewText: {
    color: CCColors.GameReview.DarkGoldText,
  },
  actionBtnStudy: {
    backgroundColor: GREEN_BG,
    borderWidth: 1,
    borderColor: `${GREEN}44`,
  },
  actionBtnStudyText: {
    color: GREEN,
  },
  actionBtnAnalyzing: {
    backgroundColor: CCColors.GameReview.GreyButtonBg,
    borderWidth: 0,
  },
  actionBtnAnalyzingText: {
    color: MUTED,
  },
  actionBtnRequest: {
    backgroundColor: GOLD,
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  actionBtnRequestText: {
    color: CCColors.GameReview.DarkGoldText,
    fontSize: FontSizes[10],
  },
  actionBtnView: {
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
  },
  actionBtnViewText: {
    color: MUTED,
  },

  // ── Empty state ────────────────────────────────────────────────────────────
  emptyStateImproved: {
    alignItems: 'center',
    paddingVertical: 64,
    paddingHorizontal: 32,
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 14,
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.25,
  },
  emptyStateTitle: {
    fontSize: FontSizes[16],
    fontWeight: '700',
    color: TEXT,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateBody: {
    fontSize: FontSizes[13],
    color: MUTED,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 380,
  },

  // ── Pagination ─────────────────────────────────────────────────────────────
  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: vs(10),
  },
  paginationButton: {
    paddingVertical: vs(8),
    paddingHorizontal: vs(8),
    marginHorizontal: vs(4),
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: vs(8),
    minWidth: vs(36),
    justifyContent: 'center',
    alignItems: 'center',
  },
  paginationText: {
    fontSize: FontSizes[12],
    color: TEXT,
    fontWeight: '500',
  },
  paginationActiveButton: {
    backgroundColor: GOLD,
    borderColor: GOLD,
  },
  paginationActiveText: {
    color: CCColors.GameReview.DarkGoldText,
  },
  paginationDisabledButton: {
    backgroundColor: BG,
    borderColor: BORDER,
  },
  paginationDisabledText: {
    color: MUTED,
  },

  // ════════════════════════════════════════════════════════════════════════════
  // ANALYSIS / OTHER SCREENS — preserved from original styles.ts
  // ════════════════════════════════════════════════════════════════════════════

  mobileLine: {
    paddingVertical: 5,
  },
  yourMoves: {
    backgroundColor: CCColors.Primary.lightYellow,
    marginHorizontal: -15,
    paddingHorizontal: 15,
  },
  mobileRowBox: {
    backgroundColor: CCColors?.White,
    margin: 10,
    padding: 15,
    borderRadius: 8,
  },
  mobileResult: {
    padding: 5,
    margin: 'auto',
    color: CCColors?.TextColor.Dark,
    borderRadius: '50%',
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cellWrapperBox: {
    padding: vs(8),
    width: '100%',
    height: '100%',
  },
  cellText: {
    fontSize: FontSizes[13],
  },
  eachMoveWrapperSolve: {
    borderColor: CCColors.Grey.Border,
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: CCColors.White,
    paddingHorizontal: 5,
    paddingVertical: 15,
    paddingBottom: 0,
    paddingTop: 0,
    justifyContent: 'center',
  },
  eachMoveWrapper1: {
    borderColor: CCColors.Grey.Border,
    borderWidth: StyleSheet.hairlineWidth,
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: CCColors.White,
    padding: 15,
    paddingBottom: 0,
    paddingTop: 0,
    justifyContent: 'center',
  },
  eachMoveWrapper2: {
    borderColor: CCColors.Grey.Border,
    borderWidth: StyleSheet.hairlineWidth,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomRightRadius: 0,
    backgroundColor: CCColors.White,
    padding: 15,
    paddingBottom: 0,
    paddingTop: 0,
    justifyContent: 'center',
  },
  eachMoveInsideWrapper: {
    marginTop: 15,
    flexDirection: 'row',
  },
  AnalysisRightComponentWrapper: {
    flex: 1,
    height: '92vh',
    width: '100%',
    backgroundColor: CCColors.Dashboard.DashboardBackground,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 18,
  },
  count: {
    fontSize: 18,
  },
  playerScore: {
    flex: 1,
  },
  reviewSummary: {
    marginTop: vs(18),
    marginHorizontal: vs(18),
    width: '90%',
    backgroundColor: CCColors.White,
    padding: vs(8),
    borderRadius: vs(8),
    shadowRadius: vs(2),
    shadowOpacity: 0.5,
    shadowColor: CCColors.TextColor.Primary,
  },
  reviewSummaryHeader: {
    letterSpacing: 2,
  },
  reviewSummaryDescription: {
    marginTop: vs(8),
    letterSpacing: 1,
    lineHeight: vs(18),
  },
  playerScoreEach: {},
  rightReviewTopHeader: {
    backgroundColor: CCColors?.White,
    padding: 10,
    paddingTop: 30,
    paddingBottom: 15,
    fontSize: FontSizes[12],
    color: CCColors?.Tabs.InactiveTabFont,
    minWidth: 100,
    textAlign: 'center',
    width: '34%',
    borderBottomColor: CCColors?.Primary.Default,
    borderBottomWidth: 3,
  },
  rightReviewLeftHeader: {
    justifyContent: 'flex-start',
    padding: 10,
    width: '30%',
    color: CCColors?.White,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  selectedMove: {
    backgroundColor: CCColors.Primary.lightYellow,
    borderColor: CCColors.Primary.Default,
    borderWidth: StyleSheet.hairlineWidth,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    padding: 15,
    paddingBottom: 0,
    paddingTop: 0,
    justifyContent: 'center',
  },
  rightReviewTopHeaderSub: {
    backgroundColor: CCColors?.White,
    padding: 10,
    paddingTop: 30,
    paddingBottom: 15,
    fontSize: FontSizes[12],
    color: CCColors?.Tabs.InactiveTabFont,
    minWidth: 100,
    textAlign: 'center',
    width: '33%',
  },
  rightReviewTopHeaderSubSub: {
    fontSize: FontSizes[3],
    height: 'fit-content',
  },
  progressBarWrapper: {
    borderRightWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.2),
  },
  progressBar: {
    margin: 12,
    backgroundColor: CCColors.Primary.Light,
    height: progressBarHeight,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 3,
    borderColor: CCColors.Primary.Light,
    borderWidth: 4,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    width: '80%',
  },
  progressBarIcon: {
    height: progressBarHeight / 2,
    width: progressBarHeight / 2,
    marginRight: 4,
  },
  progressBarText: {
    lineHeight: 18,
    fontSize: 12,
    fontWeight: '500',
  },
  progressBarFilling: {
    backgroundColor: CCColors.Primary.Action,
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
  },

  cardListContainer: {
    paddingHorizontal: 16,
    width: '100%',
    alignSelf: 'center',
    maxWidth: 1200,
  },
  resultStrip: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 24,
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopLeftRadius: 13,
    borderBottomLeftRadius: 13,
    overflow: 'hidden',
  },
});

export default customStyles;
